import React, { useEffect, useRef, useState } from 'react'

import { IQuestionViewProps } from '../types'
import { ImageQuestionModelTypes } from './types'
import { DEFAULT_WIDGET_WIDTH } from '@aytm/app/swidget/base/modules/viewport/types'

import ImageAsset from '../../modules/images/ImageAsset'
import useTimelineFrame from '../../modules/timeline/useTimelineFrame'

import commonStyle from '../_common/style.module.scss'
import style from './style.module.scss'
import useSession from '../../session/useSession'
import useViewport from '../../modules/viewport/useViewport'
import TextWithOptions from '@aytm/app/swidget/base/modules/preview/components/TextWithOptions'
import usePreviewOptions from '@aytm/app/swidget/base/modules/preview/usePreviewOptions'
import imageAssetWithOptions from '@aytm/app/swidget/base/modules/preview/functions/imageAssetWithOptions'

const IMAGE_MAX_HEIGHT = 600
const IMAGE_MIN_HEIGHT = 150

const ImageView = ({
  schema,
  response,
  responseValidation,
  showValidation,
  onUpdateResponse,
  timeline,
  frame,
  frameFullscreenHeight
}: IQuestionViewProps<ImageQuestionModelTypes>) => {
  const [timer, setTimer] = useState<number>(schema.image?.min_display_time || undefined)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout>>(null)

  const session = useSession()
  const frameInteractionStart = useTimelineFrame(timeline, frame)

  const image = imageAssetWithOptions(session.rules?.sub?.[schema.id]?.[schema.id], schema.image)
  const viewport = useViewport()
  const previewOptions = usePreviewOptions()

  useEffect(
    () => () => {
      clearInterval(timerRef.current)
      clearTimeout(autoAdvanceTimerRef.current)
    },
    []
  )

  useEffect(() => {
    if (response && !response.viewed && (timer < 1 || undefined)) {
      clearInterval(timerRef.current)
      response.viewed = true
      onUpdateResponse(response)
    }
  }, [timer, response, onUpdateResponse])

  const runTimer = () => {
    if (null === timerRef.current) {
      timerRef.current = setInterval(() => {
        setTimer(remainingTime => remainingTime - 1)
      }, 1000)
    }
  }

  const runAutoAdvanceTimer = () => {
    if (null === autoAdvanceTimerRef.current) {
      autoAdvanceTimerRef.current = setTimeout(() => session.doNext(), image.auto_advance * 1000)
    }
  }

  const onLoadImage = () => {
    frameInteractionStart()
    if (image?.min_display_time) {
      runTimer()
    } else {
      response.viewed = true
      onUpdateResponse(response)
    }

    if (image?.auto_advance) {
      runAutoAdvanceTimer()
    }
  }

  const formatTime = () => {
    const date = new Date(null)
    date.setSeconds(timer)
    return date.toISOString().slice(14, 19)
  }

  const frameWidth = viewport.emulated ? viewport.width : DEFAULT_WIDGET_WIDTH.DESKTOP
  const frameHeight = viewport.autoHeight
    ? (viewport.maxAutoHeight && Number(viewport.maxAutoHeight)) || IMAGE_MAX_HEIGHT
    : viewport.mobile
      ? Math.max(frameFullscreenHeight, IMAGE_MIN_HEIGHT)
      : IMAGE_MAX_HEIGHT

  const maxImageHeight = Math.min(image?.height, frameHeight)
  const maxImageWidth = Math.min(image?.width, frameWidth)
  const imageStyle = viewport.mobile && !image?.scrolling ? { height: maxImageHeight } : {}

  return (
    <div className={`${style.imageView} ${viewport.mobile ? style.mobile : ''}`} data-testid="SW-IM">
      <div className={image?.scrolling ? style.scrolling : style.fullsized}>
        {timer > 0 && (
          <div
            data-testid="SW-IM-ValidationMessage"
            className={`${style.timer} ${commonStyle.validationMessage} ${
              showValidation && !responseValidation.valid ? commonStyle.error : ''
            } `}
          >
            {formatTime()}
          </div>
        )}
        <div data-testid="SW-IM-Image">
          {image ? (
            <ImageAsset
              asset={image}
              width={maxImageWidth}
              height={maxImageHeight}
              className={style.image}
              style={imageStyle}
              size="big"
              onFullImageLoaded={onLoadImage}
              openPreview={image?.force_full_screen}
              previewLabelTemplate={() => (
                <TextWithOptions text={schema.text} options={previewOptions} rules={session.rules?.sub?.[schema.id]} />
              )}
            />
          ) : (
            <ImageAsset
              asset={image}
              className={style.image}
              size={'medium'}
              previewLabelTemplate={() => <span dangerouslySetInnerHTML={{ __html: schema.text }} />}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default ImageView
